import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Package,
  ShoppingBag,
  Tag,
  Calendar,
  Wallet,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const userId = localStorage.getItem("userId");
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return setLoading(false);

    const fetchOrders = async () => {
      try {
        const res = await axios.get(
          `https://accessoriesgirly.onrender.com/api/orders/user/${userId}`
        );
        setOrders(res.data);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [userId]);

  if (loading)
    return (
      <p className="text-center py-10">Loading orders...</p>
    );

  if (!userId)
    return (
      <div className="text-center py-20">
        <p className="mb-4 text-lg font-medium text-gray-700">
          Please Login First
        </p>
        <button
          onClick={() => navigate("/login")}
          className="px-6 py-2 bg-pink-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          Go to Login
        </button>
      </div>
    );

  if (orders.length === 0)
    return <p className="text-center py-10">No orders yet.</p>;

  // Status steps
  const steps = [
    { key: "pending", label: "Pending", icon: Clock, color: "text-yellow-500" },
    { key: "confirmed", label: "Confirmed", icon: CheckCircle, color: "text-blue-500" },
    { key: "shipped", label: "Shipped", icon: Truck, color: "text-purple-500" },
    { key: "delivered", label: "Delivered", icon: Package, color: "text-green-600" },
    { key: "cancelled", label: "Cancelled", icon: XCircle, color: "text-red-500" },
  ];

  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6 text-center">My Orders</h1>

      <div className="space-y-8">
        {orders.map((order) => (
          <div
            key={order._id}
            className="bg-white shadow-md rounded-xl p-6 border hover:shadow-lg transition"
          >
            {/* Header */}
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Package className="w-5 h-5 text-purple-600" />
                Order #{order.refId || order._id.slice(-6)}
              </h2>
              <span className="text-sm text-gray-500 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(order.createdAt).toLocaleDateString()}
              </span>
            </div>

            {/* Items */}
            <div className="space-y-3">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 border-b pb-2">
                  <img
                    src={item.image || "/placeholder.png"}
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded-lg border"
                  />
                  <div className="flex-1">
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-gray-600">
                      ₹{item.price} × {item.quantity}
                    </p>
                  </div>
                  <p className="font-semibold">
                    ₹{item.price * item.quantity}
                  </p>
                </div>
              ))}
            </div>

            {/* Prices */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <p className="flex items-center gap-2 font-medium">
                <ShoppingBag className="w-4 h-4 text-green-600" />
                Total: <span className="font-bold">₹{order.totalAmount}</span>
              </p>
              {order.voucher && (
                <p className="flex items-center gap-2 text-blue-600">
                  <Tag className="w-4 h-4" />
                  Voucher: -₹{order.discount}
                </p>
              )}
              <p className="flex items-center gap-2 text-purple-700 font-semibold">
                <Wallet className="w-4 h-4" />
                Final: ₹{order.finalAmount}
              </p>
              <p className="text-right text-sm text-gray-500">
                Status:{" "}
                <span className="capitalize font-semibold">{order.status}</span>
              </p>
            </div>

            {/* Tracking Timeline */}
            <div className="mt-6">
              <div className="relative flex items-center justify-between">
                {steps.map((step, idx) => {
                  const Icon = step.icon;
                  const isActive =
                    order.status === step.key ||
                    (order.statusTimestamps &&
                      order.statusTimestamps[step.key]);

                  return (
                    <div key={idx} className="flex-1 text-center relative">
                      <div
                        className={`w-10 h-10 mx-auto rounded-full border-2 flex items-center justify-center ${isActive ? step.color : "text-gray-400 border-gray-300"
                          }`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <p className="mt-2 text-sm font-medium">{step.label}</p>
                      {order.statusTimestamps?.[step.key] && (
                        <p className="text-xs text-gray-500">
                          {new Date(
                            order.statusTimestamps[step.key]
                          ).toLocaleString()}
                        </p>
                      )}

                      {/* Progress Line */}
                      {idx < steps.length - 1 && (
                        <div
                          className={`absolute top-5 left-1/2 w-full h-1 -z-10 ${order.statusTimestamps?.[steps[idx + 1].key]
                            ? "bg-green-500"
                            : "bg-gray-300"
                            }`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Truck Animation when shipped */}
              {order.status === "shipped" && (
                <div className="mt-6 flex justify-center">
                  <Truck className="w-10 h-10 text-purple-600 animate-bounce" />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
